/**
 * Phase 7.1 — gateway disconnect banner.
 *
 * The Header already shows a small at-a-glance gateway indicator
 * (Wifi icon + state label). For users who don't watch the Header,
 * a prolonged disconnect can otherwise pass unnoticed until they
 * try to send a chat / open a case and get an inscrutable RPC
 * timeout. This banner mounts a prominent strip above `<main>` once
 * the connection has been non-`connected` for >10 seconds, with a
 * state-specific message describing what's happening.
 *
 * Auto-dismisses when state returns to `connected`. No manual
 * "Reconectar" button — the GatewayClient's auto-reconnect with
 * exponential backoff is already running in the background, so a
 * manual button would mostly mirror what's already happening
 * without changing behavior. (And in the corner case where the
 * client was deliberately disconnected with `disconnect()`, the
 * `closed` flag would make `connect()` no-op anyway, so the
 * button would silently fail.)
 *
 * The 10s debounce ignores transient blips — Vite HMR reloads,
 * WebSocket pings, momentary network jitter — so the banner
 * surfaces only when the disconnect is real enough to matter.
 * Crucially, the timer tracks *continuous* non-`connected` time:
 * transitions among non-connected states (disconnected →
 * reconnecting → error) preserve the running timer, so a
 * connection oscillating without ever stabilizing for 10s still
 * trips the banner once total non-connected time crosses the
 * threshold.
 */

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGatewayStore } from "@/stores/useGatewayStore";
import type { ConnectionState } from "@/lib/gateway-client";

const DISCONNECT_GRACE_MS = 10_000;

/** Map a connection state to its i18n key. The default fallback path
 *  uses `errors.gateway.default` ("no connection") for any state not
 *  explicitly listed — matches the previous switch's `default` arm. */
function i18nKeyFor(state: ConnectionState): string {
  switch (state) {
    case "connecting":
      return "errors.gateway.connecting";
    case "authenticating":
      return "errors.gateway.authenticating";
    case "reconnecting":
      return "errors.gateway.reconnecting";
    case "error":
      return "errors.gateway.error";
    case "disconnected":
      return "errors.gateway.disconnected";
    // ⚖ TERMINAL. It must NOT fall through to the "reconnecting" copy, which
    // makes two promises the system cannot keep: that the problem is the
    // connection, and that waiting will fix it. Neither is true of a refused
    // credential — see `lib/auth-close.ts`.
    case "unauthorized":
      return "errors.gateway.unauthorized";
    default:
      return "errors.gateway.default";
  }
}

export function GatewayDisconnectedBanner() {
  const { t } = useTranslation();
  const connectionState = useGatewayStore((s) => s.connectionState);
  const maintenance = useGatewayStore((s) => s.maintenance);
  const [showBanner, setShowBanner] = useState(false);
  // Hold the timer in a ref so it survives state transitions among
  // non-connected states. If we returned `clearTimeout` from the
  // useEffect, the cleanup would fire on every state change and
  // restart the 10s countdown — so a connection oscillating
  // disconnected → reconnecting → error → disconnected every 9s
  // would never trip the banner. With a ref, the timer is started
  // once when we first leave `connected` and only cleared when we
  // return to `connected` (or on unmount).
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (connectionState === "connected") {
      setShowBanner(false);
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    // Non-connected: start the debounce timer iff one isn't already
    // running. Subsequent transitions among non-connected states
    // leave the existing timer in place.
    if (timerRef.current === null) {
      timerRef.current = setTimeout(() => {
        setShowBanner(true);
        timerRef.current = null;
      }, DISCONNECT_GRACE_MS);
    }
  }, [connectionState]);

  // Cleanup-on-unmount only — the per-state-change cleanup is handled
  // by the timer ref above so it doesn't reset on transitions.
  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  if (connectionState === "connected") return null;

  // Calm maintenance surface — a planned per-user-runtime recreate
  // (gateway closed 1008; auto-recovering). Shown PROMPTLY (no 10s
  // alarming grace) because the copy is reassuring, not scary, and a
  // silent calm strip beats a frozen-looking app. Neutral/blue, a
  // spinning refresh glyph, no AlertTriangle. This is the single
  // surface the convergence plan's Layer 2 wants instead of the
  // alarming banner — alarming surfaces are suppressed while it shows.
  if (maintenance) {
    return (
      <div
        data-testid="gateway-maintenance-banner"
        data-connection-state={connectionState}
        role="status"
        aria-live="polite"
        className="flex items-center gap-2 border-b border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-800 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-200"
      >
        <RefreshCw size={14} className="shrink-0 animate-spin" aria-hidden="true" />
        <span className="flex-1">{t("errors.gateway.maintenance")}</span>
      </div>
    );
  }

  if (!showBanner) return null;

  return (
    <div
      data-testid="gateway-disconnected-banner"
      data-connection-state={connectionState}
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <AlertTriangle size={14} className="shrink-0" aria-hidden="true" />
      <span className="flex-1">{t(i18nKeyFor(connectionState))}</span>
    </div>
  );
}
